/**
 * `{{placeholder}}` tokenizer for dashboard tile SQL (S10).
 *
 * A real scanner, not a regex over the whole text: placeholders inside string
 * literals, quoted identifiers, and comments are NOT placeholders — a tile
 * titled `SELECT '{{fake}}' AS demo` must run untouched. The main-process
 * compose step (electron/dashboards/composeQuery.ts) and the renderer's
 * parameter derivation both consume this module, so the two sides can never
 * disagree about which tokens are live.
 *
 * Per-engine syntax handled:
 * - line comments: `--` (all SQL engines; on mysql only when followed by
 *   whitespace/EOL — `a--b` is an expression there), `//` and `--`-less Kusto
 *   (kusto uses `//` only — `--` is NOT a comment in KQL)
 * - block comments (slash-star … star-slash): nested for pgsql + mssql (both nest),
 *   non-nested for mysql/sqlite/oracle; kusto has no block comments
 * - string literals: `'...'` with `''` doubling everywhere; mysql adds
 *   backslash escapes; pgsql escape strings `E'...'` honor backslashes;
 *   oracle alternative quotes `q'[...]'`; kusto uses `'...'`/`"..."` with
 *   backslash escapes
 * - quoted identifiers: `"..."` (pgsql/oracle/sqlite/mssql), `[...]` with
 *   `]]` doubling (mssql/sqlite), backticks (mysql/sqlite)
 * - pgsql dollar-quoted strings `$tag$ ... $tag$`
 */

export type PlaceholderOccurrence = {
  /** Trimmed token name between the braces. */
  name: string;
  /** Absolute offset of the opening `{{`. */
  start: number;
  /** Absolute offset just past the closing `}}`. */
  end: number;
};

const PLACEHOLDER_NAME = /^\{\{\s*([\w.-]+)\s*\}\}/;

type EngineSyntax = {
  lineComments: string[];
  blockComments: boolean;
  nestedBlockComments: boolean;
  backslashInStrings: boolean;
  doubleQuoteIsString: boolean; // kusto: "..." is a string, not an identifier
  brackets: boolean;
  backticks: boolean;
  dollarQuotes: boolean;
  /** mysql: `--` opens a comment only when followed by whitespace or EOL. */
  dashCommentNeedsWhitespace: boolean;
  /** pgsql: `E'...'` escape strings honor backslash escapes. */
  eStrings: boolean;
  /** oracle: `q'[...]'` alternative quoting. */
  qQuotes: boolean;
};

const BASE_SYNTAX: EngineSyntax = {
  lineComments: ['--'],
  blockComments: true,
  nestedBlockComments: false,
  backslashInStrings: false,
  doubleQuoteIsString: false,
  brackets: false,
  backticks: false,
  dollarQuotes: false,
  dashCommentNeedsWhitespace: false,
  eStrings: false,
  qQuotes: false,
};

const syntaxFor = (engine: string): EngineSyntax => {
  switch (engine) {
    case 'kusto':
      return {
        ...BASE_SYNTAX,
        lineComments: ['//'],
        blockComments: false,
        backslashInStrings: true,
        doubleQuoteIsString: true,
      };
    case 'mysql':
      return {
        ...BASE_SYNTAX,
        lineComments: ['--', '#'],
        backslashInStrings: true,
        backticks: true,
        dashCommentNeedsWhitespace: true,
      };
    case 'mssql':
      return { ...BASE_SYNTAX, nestedBlockComments: true, brackets: true };
    case 'pgsql':
      return { ...BASE_SYNTAX, nestedBlockComments: true, dollarQuotes: true, eStrings: true };
    case 'sqlite':
      return { ...BASE_SYNTAX, brackets: true, backticks: true };
    case 'oracle':
      return { ...BASE_SYNTAX, qQuotes: true };
    default:
      // unknown engines: conservative ANSI SQL
      return BASE_SYNTAX;
  }
};

const Q_QUOTE_CLOSERS: Record<string, string> = { '[': ']', '{': '}', '(': ')', '<': '>' };

const skipQQuote = (sql: string, from: number): number => {
  // `from` sits on the q/Q; caller verified sql[from+1] === "'". The next
  // char is the delimiter; the literal ends at `<closer>'`.
  const delim = sql[from + 2];
  if (delim === undefined) return sql.length;
  const closer = `${Q_QUOTE_CLOSERS[delim] ?? delim}'`;
  const end = sql.indexOf(closer, from + 3);
  return end === -1 ? sql.length : end + closer.length;
};

const isWordChar = (ch: string | undefined): boolean => (ch ? /[A-Za-z0-9_$]/.test(ch) : false);

const skipQuoted = (sql: string, from: number, quote: string, backslash: boolean): number => {
  // `from` sits ON the opening quote; returns index just past the closer.
  let i = from + 1;
  while (i < sql.length) {
    if (backslash && sql[i] === '\\') {
      i += 2;
      continue;
    }
    if (sql[i] === quote) {
      if (sql[i + 1] === quote) {
        i += 2; // doubled-quote escape
        continue;
      }
      return i + 1;
    }
    i += 1;
  }
  return i; // unterminated — consume to end, nothing after can be a placeholder
};

const skipBlockComment = (sql: string, from: number, nested: boolean): number => {
  let depth = 1;
  let i = from + 2;
  while (i < sql.length && depth > 0) {
    if (nested && sql[i] === '/' && sql[i + 1] === '*') {
      depth += 1;
      i += 2;
    } else if (sql[i] === '*' && sql[i + 1] === '/') {
      depth -= 1;
      i += 2;
    } else {
      i += 1;
    }
  }
  return i;
};

const skipDollarQuote = (sql: string, from: number): number => {
  // `from` sits on `$`; a dollar quote opens only if `$tag$` matches here.
  const open = /^\$([A-Za-z_][A-Za-z0-9_]*)?\$/.exec(sql.slice(from));
  if (!open) return from + 1;
  const closer = open[0];
  const end = sql.indexOf(closer, from + closer.length);
  return end === -1 ? sql.length : end + closer.length;
};

/** All live `{{name}}` occurrences in `sql`, in source order. */
export function findPlaceholders(sql: string, engine: string): PlaceholderOccurrence[] {
  const syntax = syntaxFor(engine);
  const out: PlaceholderOccurrence[] = [];
  let i = 0;
  while (i < sql.length) {
    const ch = sql[i];
    const two = sql.slice(i, i + 2);

    const commentMarker = syntax.lineComments.find((m) => sql.startsWith(m, i));
    if (commentMarker) {
      // mysql: `--` needs following whitespace/EOL (`score--penalty` is math).
      const next = sql[i + commentMarker.length];
      const opens =
        commentMarker !== '--' ||
        !syntax.dashCommentNeedsWhitespace ||
        next === undefined ||
        /\s/.test(next);
      if (opens) {
        const nl = sql.indexOf('\n', i);
        i = nl === -1 ? sql.length : nl + 1;
        continue;
      }
    }
    if (syntax.blockComments && two === '/*') {
      i = skipBlockComment(sql, i, syntax.nestedBlockComments);
      continue;
    }
    if (syntax.qQuotes && (ch === 'q' || ch === 'Q') && sql[i + 1] === "'" && !isWordChar(sql[i - 1])) {
      i = skipQQuote(sql, i);
      continue;
    }
    if (ch === "'") {
      // pgsql escape strings: a standalone E/e prefix turns backslashes on
      // for THIS literal only (`E'O\'Brien'`).
      const isEString =
        syntax.eStrings && (sql[i - 1] === 'E' || sql[i - 1] === 'e') && !isWordChar(sql[i - 2]);
      i = skipQuoted(sql, i, "'", syntax.backslashInStrings || isEString);
      continue;
    }
    if (ch === '"') {
      // Double quotes always delimit a region (string on kusto, identifier
      // elsewhere) — either way, placeholders inside are inert.
      i = skipQuoted(sql, i, '"', syntax.doubleQuoteIsString && syntax.backslashInStrings);
      continue;
    }
    if (syntax.backticks && ch === '`') {
      i = skipQuoted(sql, i, '`', false);
      continue;
    }
    if (syntax.brackets && ch === '[') {
      // [ident]] ] uses ]] doubling; scan to the closing ]
      let j = i + 1;
      while (j < sql.length) {
        if (sql[j] === ']') {
          if (sql[j + 1] === ']') {
            j += 2;
            continue;
          }
          j += 1;
          break;
        }
        j += 1;
      }
      i = j;
      continue;
    }
    if (syntax.dollarQuotes && ch === '$') {
      i = skipDollarQuote(sql, i);
      continue;
    }
    if (two === '{{') {
      const match = PLACEHOLDER_NAME.exec(sql.slice(i));
      if (match) {
        out.push({ name: match[1], start: i, end: i + match[0].length });
        i += match[0].length;
        continue;
      }
    }
    i += 1;
  }
  return out;
}

/** Distinct live placeholder names, in first-appearance order. */
export function placeholderNames(sql: string, engine: string): string[] {
  const seen = new Set<string>();
  for (const occ of findPlaceholders(sql, engine)) seen.add(occ.name);
  return [...seen];
}
